import { DataBlock, PageSection } from '@/common'
import { EstimatedCbamLiabilityWidget } from '@/shared/components'
import useI18n from '@/shared/hooks/usei18n'
import { useNetMassDisplay } from '@/shared/netMassDisplay/NetMassDisplayUnitContext'
import type { BatchLiabilityResponse } from '@/features/cbam-reporting/hooks/cbamLiability'
import type { ImportedGood } from '@/features/cbam-reporting/hooks/importedGoods'
import type { SupplierEmissionsReport } from '@/features/suppliers/hooks/supplierEmissionsReport'

type GoodRow = ImportedGood & { emissionsReport: SupplierEmissionsReport | null }

type Props = {
  reportingPeriodYear: number
  liabilityData: BatchLiabilityResponse | undefined
  isBatchLiabilityLoading: boolean
  goods: GoodRow[]
  totalCertificatesRequiredDisplay: string
}

export default function CbamReportLiabilityLegacyLayout({
  reportingPeriodYear,
  liabilityData,
  isBatchLiabilityLoading,
  goods,
  totalCertificatesRequiredDisplay
}: Props) {
  const { t, formatCbamTco2Display } = useI18n()
  const { formatNetMass } = useNetMassDisplay()

  return (
    <>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <DataBlock
          label={t('cbamReporting:cbamReportDetails.dataSections.volume')}
          data={formatNetMass(
            liabilityData?.aggregatedProjection[reportingPeriodYear]?.volume ?? 0
          )}
          isLoading={isBatchLiabilityLoading}
        />
        <DataBlock
          label={t('cbamReporting:cbamReportDetails.dataSections.totalIncludedEmbeddedEmissions')}
          data={formatCbamTco2Display(
            liabilityData?.aggregatedProjection[reportingPeriodYear]?.totalEmissions ?? 0
          )}
          isLoading={isBatchLiabilityLoading}
        />
        <DataBlock
          label={t('cbamReporting:cbamReportDetails.dataSections.totalFreeAllocationAdjustment')}
          data={formatCbamTco2Display(
            liabilityData?.aggregatedProjection[reportingPeriodYear]?.freeAllocationTCo2 ?? 0
          )}
          isLoading={isBatchLiabilityLoading}
        />
        <DataBlock
          label={t('cbamReporting:cbamReportDetails.dataSections.totalCertificatesRequired')}
          data={totalCertificatesRequiredDisplay}
          isLoading={isBatchLiabilityLoading}
        />
      </div>
      <PageSection title={t('cbamReporting:cbamReportDetails.estimatedEmissionsLiability.title')}>
        <EstimatedCbamLiabilityWidget
          items={goods.map(good => ({
            code: good.cnCode,
            country: good.country,
            productionMethodCode: good.productionMethodCode,
            netMassInTonnes: good.netMassInTonnes,
            isUsingDefaultEmissions: good.isUsingDefaultEmissions,
            emissionsReport: good.emissionsReport ?? undefined
          }))}
        />
      </PageSection>
    </>
  )
}
